import { useEffect, useState } from 'react';
import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { Crown, Flame, Target, Trophy } from 'lucide-react';
import { db } from '../../../firebase/config';
import { cn } from '../../../lib/utils';
import { Card, CardContent } from '../../ui/card';

const GlobalLeaderboard = ({ currentUserId, max = 50 }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(
      collection(db, 'userDailyChallengeScores'),
      orderBy('totalPoints', 'desc'),
      limit(max)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((r) => (r.totalPoints || 0) > 0);
        setRows(items);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, [max]);

  return (
    <Card className="border-border/60 bg-background/40">
      <CardContent className="flex flex-col gap-3 p-4">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
            <Crown className="h-3 w-3 text-heat" />
            Global leaderboard
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
            {rows.length} {rows.length === 1 ? 'player' : 'players'}
          </span>
        </div>

        {loading ? (
          <div className="space-y-1.5">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-9 animate-pulse rounded-sm bg-muted/50" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <p className="py-4 text-center text-xs text-muted-foreground">
            No scores yet — be the first.
          </p>
        ) : (
          <table className="w-full text-sm tabular-nums">
            <thead>
              <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <th className="w-8 py-1.5 text-left">#</th>
                <th className="py-1.5 text-left">Player</th>
                <th className="py-1.5 text-right">Points</th>
                <th className="hidden py-1.5 text-center sm:table-cell">
                  <span className="inline-flex items-center justify-center gap-1">
                    <Trophy className="h-3 w-3" />
                    Plays
                  </span>
                </th>
                <th className="py-1.5 text-center">
                  <span className="inline-flex items-center justify-center gap-1">
                    <Target className="h-3 w-3" />
                    <span className="hidden sm:inline">Wins</span>
                    <span className="sm:hidden">W</span>
                  </span>
                </th>
                <th className="hidden py-1.5 text-right sm:table-cell">
                  <span className="inline-flex items-center justify-end gap-1">
                    <Flame className="h-3 w-3" />
                    Best
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const isMe = currentUserId && r.id === currentUserId;
                const rank = i + 1;
                return (
                  <tr key={r.id} className={cn('border-t border-border/40', isMe && 'bg-heat/10')}>
                    <td className="py-2 font-mono text-xs text-muted-foreground">
                      {rank === 1 ? (
                        <span className="text-gold">1</span>
                      ) : rank === 2 ? (
                        <span className="text-silver">2</span>
                      ) : rank === 3 ? (
                        <span className="text-bronze">3</span>
                      ) : (
                        rank
                      )}
                    </td>
                    <td className="py-2 font-medium">
                      <span className={cn('truncate', isMe && 'text-heat')}>
                        {r.username || 'Anonymous'}
                        {isMe && (
                          <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.2em] text-heat">
                            you
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="py-2 text-right font-semibold">{r.totalPoints ?? 0}</td>
                    <td className="hidden py-2 text-center text-muted-foreground sm:table-cell">
                      {r.totalChallenges ?? 0}
                    </td>
                    <td className="py-2 text-center text-muted-foreground">
                      {r.correctGuesses ?? 0}
                    </td>
                    <td className="hidden py-2 text-right text-muted-foreground sm:table-cell">
                      {r.bestStreak ?? 0}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
};

export default GlobalLeaderboard;
